import type {
  CrawlJobNumericStatsDTO,
  CrawlJobTenantAnalyticsDTO,
} from '@liks/lac-svc-api/esm/models/index';
import { useMemo } from 'react';
import { useI18n } from '../../../i18n/useI18n';
import {
  formatBytes,
  formatDuration,
  formatNumber,
} from '../../../utils/formatUtils';
import {
  formatCrawlJobStatusLabel,
  sortCrawlJobStatusCounts,
  sumCrawlJobStatusCounts,
} from '../crawlJobAnalyticsUtils';
import { CrawlStatusBar } from './CrawlStatusBar';
import './LacCrawlJobAnalyticsSummary.css';

type LacCrawlJobAnalyticsSummaryProps = {
  query: {
    data?: CrawlJobTenantAnalyticsDTO | null;
    isLoading: boolean;
    isError: boolean;
  };
};

function hasNumericStats(
  stats: CrawlJobNumericStatsDTO | null | undefined,
): stats is CrawlJobNumericStatsDTO {
  return (
    stats != null &&
    (stats.min != null || stats.avg != null || stats.max != null)
  );
}

type NumericStatRow = {
  key: string;
  title: string;
  stats: CrawlJobNumericStatsDTO;
  formatValue: (value: number) => string;
};

type NumericStatsGridProps = {
  rows: NumericStatRow[];
  minLabel: string;
  avgLabel: string;
  maxLabel: string;
};

function formatNumericStatsAriaLabel(
  stats: CrawlJobNumericStatsDTO,
  formatValue: (value: number) => string,
  minLabel: string,
  avgLabel: string,
  maxLabel: string,
): string {
  const parts: string[] = [];
  if (stats.avg != null) {
    parts.push(`${avgLabel}: ${formatValue(Math.round(stats.avg))}`);
  }
  if (stats.min != null) {
    parts.push(`${minLabel}: ${formatValue(stats.min)}`);
  }
  if (stats.max != null) {
    parts.push(`${maxLabel}: ${formatValue(stats.max)}`);
  }
  return parts.join(', ');
}

function NumericStatsGrid({
  rows,
  minLabel,
  avgLabel,
  maxLabel,
}: NumericStatsGridProps) {
  if (rows.length === 0) {
    return null;
  }

  return (
    <div
      className="lac-crawl-job-analytics-summary__numeric"
      aria-label={rows.map((row) => row.title).join(', ')}
    >
      {rows.map((row) => (
        <div
          key={row.key}
          className="lac-crawl-job-analytics-summary__numeric-group"
        >
          <div className="lac-crawl-job-analytics-summary__numeric-label">
            {row.title}
          </div>
          <div
            className="lac-crawl-job-analytics-summary__numeric-values"
            aria-label={formatNumericStatsAriaLabel(
              row.stats,
              row.formatValue,
              minLabel,
              avgLabel,
              maxLabel,
            )}
          >
            {row.stats.avg != null ? (
              <span className="lac-crawl-job-analytics-summary__numeric-part">
                <span
                  className="lac-crawl-job-analytics-summary__numeric-symbol"
                  aria-hidden="true"
                >
                  ø
                </span>
                {row.formatValue(Math.round(row.stats.avg))}
              </span>
            ) : null}
            {row.stats.min != null ? (
              <span className="lac-crawl-job-analytics-summary__numeric-part">
                <span
                  className="lac-crawl-job-analytics-summary__numeric-symbol"
                  aria-hidden="true"
                >
                  ↓
                </span>
                {row.formatValue(row.stats.min)}
              </span>
            ) : null}
            {row.stats.max != null ? (
              <span className="lac-crawl-job-analytics-summary__numeric-part">
                <span
                  className="lac-crawl-job-analytics-summary__numeric-symbol"
                  aria-hidden="true"
                >
                  ↑
                </span>
                {row.formatValue(row.stats.max)}
              </span>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
}

export function LacCrawlJobAnalyticsSummary({
  query,
}: LacCrawlJobAnalyticsSummaryProps) {
  const { locale, translate } = useI18n();

  const statusCounts = useMemo(
    () => sortCrawlJobStatusCounts(query.data?.statusCounts ?? []),
    [query.data?.statusCounts],
  );
  const totalCount = sumCrawlJobStatusCounts(statusCounts);

  const resolveStatusLabel = (status: string | null | undefined) =>
    formatCrawlJobStatusLabel(status, translate);

  if (query.isLoading) {
    return (
      <aside
        className="lac-crawl-job-analytics-summary"
        aria-label={translate('lac.websiteCrawls.chart.analytics.legend')}
      >
        <p className="lac-crawl-job-analytics-summary__status">
          {translate('lac.websiteCrawls.chart.analytics.loading')}
        </p>
      </aside>
    );
  }

  if (query.isError) {
    return (
      <aside
        className="lac-crawl-job-analytics-summary"
        aria-label={translate('lac.websiteCrawls.chart.analytics.legend')}
      >
        <p className="lac-crawl-job-analytics-summary__status" role="alert">
          {translate('lac.websiteCrawls.chart.analytics.loadError')}
        </p>
      </aside>
    );
  }

  const minLabel = translate('lac.websiteCrawls.chart.analytics.min');
  const avgLabel = translate('lac.websiteCrawls.chart.analytics.avg');
  const maxLabel = translate('lac.websiteCrawls.chart.analytics.max');

  const numericRows: NumericStatRow[] = [];
  const durationStats = query.data?.durationMillis;
  if (hasNumericStats(durationStats)) {
    numericRows.push({
      key: 'duration',
      title: translate('lac.websiteCrawls.chart.analytics.duration'),
      stats: durationStats,
      formatValue: (value) => formatDuration(value, locale),
    });
  }
  const urlCountStats = query.data?.selectedUrlCount;
  if (hasNumericStats(urlCountStats)) {
    numericRows.push({
      key: 'urlCount',
      title: translate('lac.websiteCrawls.chart.analytics.urlCount'),
      stats: urlCountStats,
      formatValue: (value) => formatNumber(value, locale),
    });
  }
  const sizeStats = query.data?.totalSizeBytes;
  if (hasNumericStats(sizeStats)) {
    numericRows.push({
      key: 'totalSize',
      title: translate('lac.websiteCrawls.chart.analytics.totalSize'),
      stats: sizeStats,
      formatValue: (value) => formatBytes(value, locale),
    });
  }

  return (
    <aside
      className="lac-crawl-job-analytics-summary"
      aria-label={translate('lac.websiteCrawls.chart.analytics.legend')}
    >
      <div className="lac-crawl-job-analytics-summary__total">
        <span className="lac-crawl-job-analytics-summary__total-label">
          {translate('lac.websiteCrawls.chart.analytics.total')}
        </span>
        <span className="lac-crawl-job-analytics-summary__total-value">
          {formatNumber(totalCount, locale)}
        </span>
      </div>

      <CrawlStatusBar
        className="crawl-status-bar--full-width"
        statusCounts={statusCounts}
        locale={locale}
        resolveStatusLabel={resolveStatusLabel}
        ariaLabel={translate('lac.websiteCrawls.chart.analytics.statusBar')}
      />

      <NumericStatsGrid
        rows={numericRows}
        minLabel={minLabel}
        avgLabel={avgLabel}
        maxLabel={maxLabel}
      />
    </aside>
  );
}
